import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";

const ADMIN_ROLES = ["super_admin", "finance_manager", "vehicle_manager", "support_agent"];

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ username: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = mode === "login"
        ? await login(form.username, form.password)
        : await register({ username: form.username, password: form.password, name: form.name });
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate(ADMIN_ROLES.includes(u.role) ? "/admin" : "/dashboard");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="container-x flex min-h-[80vh] items-center justify-center py-16">
        <div className="card-surface w-full max-w-md p-8">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl gold-gradient text-white"><Lock className="h-7 w-7" /></div>
          <h1 className="mt-5 text-center font-display text-2xl font-bold text-slate-900">{mode === "login" ? "Sign In" : "Create Account"}</h1>
          <p className="mt-1 text-center text-sm text-slate-600">{mode === "login" ? "Enter your username and code to continue" : "Register to start a financing application"}</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm text-slate-700">Username</label>
              <Input data-testid="login-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required autoComplete="username" className="bg-white" />
            </div>
            {mode === "register" && (
              <div>
                <label className="mb-1.5 block text-sm text-slate-700">Full Name</label>
                <Input data-testid="login-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white" />
              </div>
            )}
            <div>
              <label className="mb-1.5 block text-sm text-slate-700">Code</label>
              <Input data-testid="login-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required autoComplete={mode === "login" ? "current-password" : "new-password"} className="bg-white" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="btn-gold w-full rounded-full py-6 text-base">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "login" ? "Sign In" : "Create Account")}
            </Button>
          </form>

          <div className="mt-5 text-center text-sm text-slate-600">
            {mode === "login" ? (
              <button onClick={() => setMode("register")} data-testid="switch-register" className="font-medium text-gold hover:underline">New customer? Create an account</button>
            ) : (
              <button onClick={() => setMode("login")} data-testid="switch-login" className="font-medium text-gold hover:underline">Already have an account? Sign in</button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
